import { useLayoutEffect, useState, useRef } from 'react'
import type { InventoryItemData } from '../types'

interface GridConfig {
  columnsCount: number
  filledSlots: (InventoryItemData | null)[]
  gridTemplateColumns: string
  contentRef: React.RefObject<HTMLDivElement>
}

interface GridItemInfo {
  isLastColumn: boolean
  isLastRow: boolean
}

const calculateGridLayout = (
  items: InventoryItemData[],
  containerWidth: number
) => {
  const itemWidth = 72 // Fixed item width
  const cellPadding = 16 // 8px padding on each side
  
  // Calculate minimum width needed for 4 columns
  const minWidthFor4Columns = (itemWidth + cellPadding) * 4
  
  // Use 4 columns if container is wide enough, otherwise 3
  const columnsCount = containerWidth >= minWidthFor4Columns ? 4 : 3
  
  // Calculate slots needed: complete rows + empty slots for incomplete rows
  const filledSlots: (InventoryItemData | null)[] = [...items]
  
  if (items.length === 0) {
    // If no items, show first row of empty slots
    while (filledSlots.length < columnsCount) {
      filledSlots.push(null)
    }
  } else {
    // If there are items, complete any partial row with empty slots
    const remainder = items.length % columnsCount
    if (remainder > 0) {
      const emptySlots = columnsCount - remainder
      for (let i = 0; i < emptySlots; i++) {
        filledSlots.push(null)
      }
    }
  }

  return {
    columnsCount,
    filledSlots,
    gridTemplateColumns: `repeat(${columnsCount}, 1fr)`
  }
}

export const getGridItemInfo = (
  index: number,
  columnsCount: number,
  totalSlots: number
): GridItemInfo => {
  const columnIndex = index % columnsCount
  const rowIndex = Math.floor(index / columnsCount)
  const isLastColumn = columnIndex === columnsCount - 1
  const totalRows = Math.ceil(totalSlots / columnsCount)
  const isLastRow = rowIndex === totalRows - 1
  
  return {
    isLastColumn,
    isLastRow
  }
}

export const useInventoryGrid = (items: InventoryItemData[]): GridConfig => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [columnsCount, setColumnsCount] = useState(3)

  useLayoutEffect(() => {
    const updateColumns = () => {
      if (contentRef.current) {
        const containerWidth = contentRef.current.offsetWidth
        const { columnsCount: newColumnsCount } = calculateGridLayout(items, containerWidth)
        setColumnsCount(newColumnsCount)
      }
    }

    updateColumns()
    window.addEventListener('resize', updateColumns)
    return () => {
      window.removeEventListener('resize', updateColumns)
    }
  }, [items])

  const { filledSlots, gridTemplateColumns } = calculateGridLayout(
    items, 
    contentRef.current?.offsetWidth || 315
  )

  return {
    columnsCount,
    filledSlots,
    gridTemplateColumns,
    contentRef
  }
}